import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import FAQSchema from "@/components/schema/FAQSchema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Check,
  X,
  Minus,
  Clock,
  DollarSign,
  Brain,
  Users,
  Zap,
  Target,
  BarChart3,
  Shield,
  RefreshCw,
  Globe,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { getAuthUrl } from "@/utils/redirectUtils";

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

interface FeatureRow {
  feature: string;
  upfixe: "yes" | "no" | "partial" | string;
  manual: "yes" | "no" | "partial" | string;
  agency: "yes" | "no" | "partial" | string;
  otherTools: "yes" | "no" | "partial" | string;
}

const featureRows: FeatureRow[] = [
  { feature: "Budget-based keyword filtering", upfixe: "yes", manual: "no", agency: "partial", otherTools: "no" },
  { feature: "CPA vs bid estimate comparison", upfixe: "yes", manual: "no", agency: "partial", otherTools: "no" },
  { feature: "Search volume prioritisation", upfixe: "yes", manual: "partial", agency: "yes", otherTools: "partial" },
  { feature: "Thematic ad group creation", upfixe: "yes", manual: "partial", agency: "yes", otherTools: "no" },
  { feature: "AI-generated ad copy", upfixe: "yes", manual: "no", agency: "yes", otherTools: "partial" },
  { feature: "Value proposition analysis", upfixe: "yes", manual: "no", agency: "partial", otherTools: "no" },
  { feature: "5-minute campaign setup", upfixe: "yes", manual: "no", agency: "no", otherTools: "partial" },
  { feature: "Multi-source keyword pipeline", upfixe: "yes", manual: "no", agency: "partial", otherTools: "partial" },
  { feature: "Automatic geo/language targeting", upfixe: "yes", manual: "partial", agency: "yes", otherTools: "partial" },
  { feature: "Real-time bid data", upfixe: "yes", manual: "yes", agency: "yes", otherTools: "partial" },
  { feature: "150+ keyword research", upfixe: "yes", manual: "no", agency: "yes", otherTools: "partial" },
  { feature: "No long-term contract", upfixe: "yes", manual: "yes", agency: "no", otherTools: "partial" },
];

const costComparison = [
  {
    name: "Upfixe",
    monthly: "$0–$249",
    setup: "$0",
    commitment: "None",
    timeToLaunch: "5 minutes",
    highlight: true,
  },
  {
    name: "DIY / Manual",
    monthly: "$0 (your time)",
    setup: "10–20 hrs learning",
    commitment: "Ongoing effort",
    timeToLaunch: "Days–weeks",
    highlight: false,
  },
  {
    name: "Agency",
    monthly: "$1,500–$5,000+",
    setup: "$500–$2,000",
    commitment: "3–12 months",
    timeToLaunch: "2–4 weeks",
    highlight: false,
  },
  {
    name: "Other Tools",
    monthly: "$99–$499",
    setup: "$0–$500",
    commitment: "Annual plans",
    timeToLaunch: "1–3 days",
    highlight: false,
  },
];

const timeComparison = [
  { task: "Keyword research (150+ keywords)", upfixe: "2 min", other: "3–4 hours" },
  { task: "Budget-based filtering", upfixe: "Automatic", other: "Manual spreadsheet" },
  { task: "Ad group organisation", upfixe: "30 sec", other: "1–2 hours" },
  { task: "Ad copy creation", upfixe: "1 min", other: "2ï¿½ï¿½3 hours" },
  { task: "Campaign launch", upfixe: "1 click", other: "30+ min setup" },
  { task: "Total time to live campaign", upfixe: "~5 min", other: "8–12+ hours" },
];

const compareFaqItems = [
  {
    question: "How is Upfixe different from Google Ads Smart Campaigns?",
    answer: "Smart Campaigns use Google's automated bidding but give you almost no control over keyword selection, ad copy, or budget allocation. Upfixe gives you the speed of automation with the strategic control of manual management — you get budget-based keyword filtering, thematic ad groups, and AI ad copy that's tailored to your value proposition, not generic templates."
  },
  {
    question: "Can Upfixe replace a Google Ads agency?",
    answer: "For most small and medium businesses spending under $5,000/month on ads, yes. Upfixe automates the keyword research, budget optimisation, and campaign structuring that agencies charge $1,500–$5,000/month to do. You get the same strategic methodology — budget-based keyword selection, thematic grouping, and performance monitoring — at a fraction of the cost and without the 3-month contract."
  },
  {
    question: "What if I'm already using another Google Ads tool?",
    answer: "Most Google Ads tools focus on bid management or reporting after your campaign is live. Upfixe is different — it handles the strategic foundation: which keywords to target based on your budget, how to group them for relevance, and what ad copy to write. This is the work that typically requires a specialist or agency, and it happens before bidding even begins."
  },
  {
    question: "Is the manual approach ever better than Upfixe?",
    answer: "If you're a certified Google Ads specialist with deep industry experience and unlimited time, manual management gives you maximum control. But for business owners, marketers, and even freelancers managing multiple accounts, the time investment of manual keyword research, budget analysis, and ad group structuring rarely justifies the marginal control advantage. Upfixe delivers 90% of expert-level strategy in 5 minutes instead of 10 hours."
  },
  {
    question: "Do I still need to monitor my campaigns with Upfixe?",
    answer: "Upfixe handles the strategic setup and keyword optimisation, but we recommend reviewing performance weekly. Upfixe's reporting dashboard shows you exactly how each ad group and keyword is performing, so monitoring takes minutes rather than hours. The heavy lifting — keyword research, budget filtering, and campaign structuring — is fully automated."
  },
];

/* ------------------------------------------------------------------ */
/*  Animated counter                                                   */
/* ------------------------------------------------------------------ */

const AnimatedTime = ({ value, inView }: { value: string; inView: boolean }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (inView) {
      const t = setTimeout(() => setShow(true), 400);
      return () => clearTimeout(t);
    }
  }, [inView]);
  return (
    <span className={`transition-all duration-500 ${show ? "opacity-100" : "opacity-0"}`}>
      {value}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Cell renderer                                                      */
/* ------------------------------------------------------------------ */

const CellIcon = ({ value }: { value: string }) => {
  if (value === "yes") return <Check className="h-5 w-5 text-green-600 mx-auto" />;
  if (value === "no") return <X className="h-5 w-5 text-red-400 mx-auto" />;
  if (value === "partial") return <Minus className="h-5 w-5 text-amber-500 mx-auto" />;
  return <span className="text-sm">{value}</span>;
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const Compare = () => {
  const timeRef = useRef<HTMLDivElement>(null);
  const timeInView = useInView(timeRef, { once: true, margin: "-50px" });

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Compare | Upfixe vs Agencies, DIY & Other Tools | Upfixe</title>
        <meta
          name="description"
          content="See how Upfixe's budget-smart Google Ads automation compares to agencies, manual management, and other tools. Feature-by-feature comparison with real cost and time data."
        />
        <meta name="keywords" content="google ads tool comparison, upfixe vs agency, google ads automation comparison, best google ads tool, google ads management cost" />
        <meta property="og:title" content="Compare | Upfixe vs Agencies, DIY & Other Tools" />
        <meta property="og:description" content="Feature-by-feature comparison of Upfixe against agencies, manual Google Ads management, and competing tools." />
        <link rel="canonical" href="https://upfixe.com/compare" />
      </Helmet>
      <FAQSchema items={compareFaqItems} />

      <Header />

      <main className="flex-grow">
        {/* ---- HERO ---- */}
        <section className="pt-32 pb-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-primary/5" />
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />

          <div className="container mx-auto max-w-5xl relative z-10 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Badge className="px-4 py-1.5 text-sm bg-primary/10 text-primary border-primary/20 mb-6">
                Honest Comparison
              </Badge>
            </motion.div>
            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              How Upfixe Compares to{" "}
              <span className="text-primary">Every Alternative</span>
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl text-muted-foreground mb-10 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Whether you're managing Google Ads yourself, paying an agency, or using another tool —
              see exactly where Upfixe delivers more value for less money and less time.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Button size="lg" className="h-14 px-8 text-lg" onClick={() => window.location.href = getAuthUrl()}>
                Try Upfixe Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="h-14 px-8 text-lg" asChild>
                <Link to="/keyword-optimisation">See Keyword Optimisation</Link>
              </Button>
            </motion.div>
          </div>
        </section>

        {/* ---- APPROACH CARDS ---- */}
        <section className="py-24 px-4 bg-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">Four Approaches</Badge>
                <h2 className="text-3xl md:text-5xl font-bold mb-6">Four Ways to Run Google Ads</h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Each approach has trade-offs. Here's an honest look at all of them.
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: <Sparkles className="h-8 w-8" />,
                  title: "Upfixe",
                  subtitle: "AI automation",
                  pros: ["5-min setup", "Budget-smart keywords", "From $0/month"],
                  cons: ["Less manual control"],
                  color: "primary",
                  highlight: true,
                },
                {
                  icon: <Clock className="h-8 w-8" />,
                  title: "DIY / Manual",
                  subtitle: "Self-managed",
                  pros: ["Full control", "No tool cost"],
                  cons: ["10+ hrs/week", "Steep learning curve", "Easy to waste budget"],
                  color: "amber",
                  highlight: false,
                },
                {
                  icon: <Users className="h-8 w-8" />,
                  title: "Agency",
                  subtitle: "Managed service",
                  pros: ["Expert strategy", "Hands-off"],
                  cons: ["$1.5K–$5K+/month", "Long contracts", "Slow to launch"],
                  color: "purple",
                  highlight: false,
                },
                {
                  icon: <Zap className="h-8 w-8" />,
                  title: "Other Tools",
                  subtitle: "Bid management",
                  pros: ["Bid automation", "Reporting"],
                  cons: ["No keyword strategy", "$99–$499/month", "Complex setup"],
                  color: "slate",
                  highlight: false,
                },
              ].map((card, index) => (
                <motion.div
                  key={card.title}
                  className={`rounded-2xl p-6 border-2 ${
                    card.highlight
                      ? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
                      : "border-border bg-white"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  {card.highlight && (
                    <Badge className="bg-primary text-white mb-3 text-xs">Recommended</Badge>
                  )}
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                    card.highlight ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}>
                    {card.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-1">{card.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{card.subtitle}</p>
                  <div className="space-y-2 mb-4">
                    {card.pros.map((pro) => (
                      <div key={pro} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>{pro}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {card.cons.map((con) => (
                      <div key={con} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <span>{con}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- FEATURE COMPARISON TABLE ---- */}
        <section className="py-24 px-4 bg-muted/30">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">Feature by Feature</Badge>
                <h2 className="text-3xl md:text-5xl font-bold mb-6">Detailed Feature Comparison</h2>
              </motion.div>
            </div>

            <motion.div
              className="bg-white rounded-2xl border border-border shadow-sm overflow-x-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-6 py-4 font-semibold text-muted-foreground">Feature</th>
                    <th className="px-4 py-4 font-bold text-primary text-center bg-primary/5">Upfixe</th>
                    <th className="px-4 py-4 font-semibold text-center">DIY</th>
                    <th className="px-4 py-4 font-semibold text-center">Agency</th>
                    <th className="px-4 py-4 font-semibold text-center">Other Tools</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {featureRows.map((row, index) => (
                    <motion.tr
                      key={row.feature}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.04 }}
                    >
                      <td className="px-6 py-3.5 font-medium">{row.feature}</td>
                      <td className="px-4 py-3.5 text-center bg-primary/5"><CellIcon value={row.upfixe} /></td>
                      <td className="px-4 py-3.5 text-center"><CellIcon value={row.manual} /></td>
                      <td className="px-4 py-3.5 text-center"><CellIcon value={row.agency} /></td>
                      <td className="px-4 py-3.5 text-center"><CellIcon value={row.otherTools} /></td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            <div className="flex gap-6 mt-4 text-xs text-muted-foreground justify-center">
              <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-green-600" /> Included</span>
              <span className="flex items-center gap-1"><Minus className="h-3.5 w-3.5 text-amber-500" /> Partial</span>
              <span className="flex items-center gap-1"><X className="h-3.5 w-3.5 text-red-400" /> Not included</span>
            </div>
          </div>
        </section>

        {/* ---- COST COMPARISON ---- */}
        <section className="py-24 px-4 bg-background">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
                  <DollarSign className="h-3.5 w-3.5 inline mr-1" />
                  Cost Breakdown
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold mb-6">What You'll Actually Pay</h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  No hidden fees, no surprise invoices. Here's the real cost of each approach.
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {costComparison.map((item, index) => (
                <motion.div
                  key={item.name}
                  className={`rounded-2xl p-6 border-2 ${
                    item.highlight
                      ? "border-primary bg-primary/5 shadow-lg"
                      : "border-border bg-white"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <h3 className={`text-lg font-bold mb-4 ${item.highlight ? "text-primary" : ""}`}>
                    {item.name}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Monthly cost</p>
                      <p className="text-xl font-bold">{item.monthly}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Setup cost</p>
                      <p className="font-semibold">{item.setup}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Commitment</p>
                      <p className="font-semibold">{item.commitment}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Time to launch</p>
                      <p className={`font-semibold ${item.highlight ? "text-primary" : ""}`}>
                        {item.timeToLaunch}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- TIME COMPARISON ---- */}
        <section className="py-24 px-4 bg-muted/30" ref={timeRef}>
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
                  <Clock className="h-3.5 w-3.5 inline mr-1" />
                  Time Saved
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold mb-6">Hours Back in Your Day</h2>
              </motion.div>
            </div>

            <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="grid grid-cols-3 px-6 py-4 bg-muted/50 border-b border-border text-sm font-semibold">
                <span>Task</span>
                <span className="text-center text-primary">Upfixe</span>
                <span className="text-center">Manual / Agency</span>
              </div>
              {timeComparison.map((row, index) => (
                <motion.div
                  key={row.task}
                  className="grid grid-cols-3 px-6 py-4 border-b border-border last:border-0 text-sm"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                >
                  <span className="font-medium">{row.task}</span>
                  <span className="text-center font-bold text-primary">
                    <AnimatedTime value={row.upfixe} inView={timeInView} />
                  </span>
                  <span className="text-center text-muted-foreground">{row.other}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- WHY UPFIXE WINS ---- */}
        <section className="py-24 px-4 bg-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">Why Upfixe</Badge>
                <h2 className="text-3xl md:text-5xl font-bold mb-6">The Upfixe Advantage</h2>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { icon: <Brain className="h-7 w-7" />, title: "AI Keyword Strategy", desc: "Budget-based filtering and thematic grouping that agencies charge $2,000+/month for — automated in 5 minutes." },
                { icon: <DollarSign className="h-7 w-7" />, title: "Fraction of the Cost", desc: "Starting free, scaling to $249/month. Compare that to $1,500–$5,000/month for an agency doing the same work." },
                { icon: <Target className="h-7 w-7" />, title: "Budget-Smart from Day One", desc: "Every keyword is filtered against your CPA target. No wasted spend on keywords you can't afford to convert on." },
                { icon: <BarChart3 className="h-7 w-7" />, title: "Transparent Reporting", desc: "See exactly which keywords perform, which ad groups convert, and where your budget goes. No black-box agency reports." },
                { icon: <Shield className="h-7 w-7" />, title: "No Lock-In", desc: "Cancel any time. No annual contracts, no setup fees, no minimum spend commitments." },
                { icon: <Globe className="h-7 w-7" />, title: "Multi-Language & Geo", desc: "Automatic geo-targeting and language adaptation. Run campaigns in any market without specialist knowledge." },
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  className="bg-white rounded-xl p-6 border border-border hover:border-primary/30 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- FAQ ---- */}
        <section className="py-24 px-4 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">FAQ</Badge>
                <h2 className="text-3xl md:text-5xl font-bold mb-6">Common Comparison Questions</h2>
              </motion.div>
            </div>

            <motion.div
              className="space-y-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {compareFaqItems.map((item, index) => (
                <details
                  key={index}
                  className="bg-white rounded-xl border border-border px-6 group"
                >
                  <summary className="py-5 font-semibold cursor-pointer list-none flex justify-between items-center">
                    {item.question}
                    <span className="text-muted-foreground group-open:rotate-180 transition-transform">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </summary>
                  <p className="text-muted-foreground leading-relaxed pb-5">{item.answer}</p>
                </details>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ---- FINAL CTA ---- */}
        <section className="py-24 px-4 bg-gradient-to-b from-primary/5 to-background relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
          <div className="container mx-auto max-w-4xl relative z-10 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Sparkles className="h-12 w-12 text-primary mx-auto mb-6" />
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                See the Difference for Yourself
              </h2>
              <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
                Your first campaign is free. No credit card, no commitment. Launch in 5 minutes
                and see how budget-smart keyword optimisation transforms your Google Ads performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button size="lg" className="h-14 px-10 text-lg" onClick={() => window.location.href = getAuthUrl()}>
                  Start Free Campaign <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" size="lg" className="h-14 px-10 text-lg" asChild>
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
              <div className="flex flex-wrap gap-4 justify-center text-sm">
                <Link to="/features" className="text-primary hover:underline">All Features</Link>
                <span className="text-muted-foreground">|</span>
                <Link to="/keyword-optimisation" className="text-primary hover:underline">Keyword Optimisation</Link>
                <span className="text-muted-foreground">|</span>
                <Link to="/how-it-works" className="text-primary hover:underline">How It Works</Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Compare;
